import { useState } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useLayout } from "../../hooks/useWindowWidth";
import { useThemeContext } from "../../theme/ThemeContext";
import { RADIUS } from "../../theme/tokens";
import { Button, inputStyle } from "../shared";
import { overlayBackdrop } from "../../theme/styles";
import { dbSubmitFeedback } from "../../db";
import { MODAL_CLOSE_DELAY_MS } from "../../utils/constants";
import { useSwipeToClose } from "../../hooks/useSwipeToClose";

export function FeedbackModal({ reporter, airline, currentPage, onClose }) {
  const { SA, GLASS, tokens, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);
  const { isWide } = useLayout();
  const { handleProps, dragStyle } = useSwipeToClose(onClose, { enabled: !isWide });
  const [category, setCategory] = useState("Bug");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const categories = ["Bug", "Issue", "Suggestion"];
  const handleSubmit = async () => {
    if (!body.trim()) return;
    setSaving(true);
    await dbSubmitFeedback({ category, body: body.trim(), reporter: reporter || "Unknown", airline: airline || "Unknown", page: currentPage || "", resolved: false });
    setSaving(false);
    setDone(true);
    setTimeout(onClose, MODAL_CLOSE_DELAY_MS);
  };
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 200, display: "flex", alignItems: isWide ? "center" : "flex-end", justifyContent: isWide ? "center" : "stretch", padding: isWide ? 24 : 0 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...GLASS.modal, borderRadius: isWide ? 16 : "16px 16px 0 0", width: isWide ? 420 : "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", animation: isWide ? "modalSlideUp 0.25s cubic-bezier(0.16,1,0.3,1)" : "sheetSlideUp 0.3s cubic-bezier(0.16,1,0.3,1)", ...dragStyle }}>
        {!isWide && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px",
                        flexShrink: 0, touchAction: "none", cursor: "grab" }} {...handleProps}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: SA.borderGray }} />
          </div>
        )}
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontFamily: "'Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.label, color: SA.silver, letterSpacing: "0.2em", textTransform: "uppercase" }}>Report Bug / Suggestion</span>
          <Button variant="icon" onClick={onClose}
            style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16 }}>×</Button>
        </div>
        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          {done ? (
            <div style={{ textAlign: "center", padding: "30px 0", color: tokens.success, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.note, letterSpacing: "0.1em" }}>✓ SUBMITTED — THANK YOU</div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", marginBottom: 8 }}>Category</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {categories.map(c => (
                    <Button key={c} onClick={() => setCategory(c)} style={{ flex: 1, padding: "8px 0", borderRadius: RADIUS.sharp, fontFamily: "'Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.label, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: category === c ? 700 : 400, background: category === c ? (c === "Bug" ? tokens.dangerBg : c === "Issue" ? "rgba(240,180,60,0.15)" : "rgba(74,124,247,0.15)") : "transparent", border: `1px solid ${category === c ? (c === "Bug" ? tokens.monoRed : c === "Issue" ? "#d4a840" : SA.accent) : SA.borderGray}`, color: category === c ? (c === "Bug" ? tokens.monoRed : c === "Issue" ? "#d4a840" : SA.accent) : SA.silver }}>{c}</Button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", marginBottom: 8 }}>Description</div>
                <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Describe the bug, issue, or suggestion..." rows={5} style={{ ...inputStyle(false, SA), width: "100%", fontFamily: "'Barlow',sans-serif", fontSize: TYPOGRAPHY.note, resize: "none", boxSizing: "border-box", outline: "none" }} />
              </div>
              <div style={{ fontSize: TYPOGRAPHY.label, color: SA.borderGray, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", letterSpacing: "0.06em" }}>
                Submitting as: <span style={{ color: SA.silver }}>{reporter}</span> · {airline} · {currentPage}
              </div>
            </>
          )}
        </div>
        {/* Footer — hidden when done */}
        {!done && (
          <div style={{ padding: "12px 20px 20px", borderTop: `1px solid ${SA.borderGray}` }}>
            <Button onClick={handleSubmit} disabled={!body.trim() || saving} style={{ width: "100%", padding: "12px", borderRadius: RADIUS.sharp, background: body.trim() && !saving ? SA.accent : "transparent", border: `1px solid ${body.trim() && !saving ? SA.accent : SA.borderGray}`, color: body.trim() && !saving ? SA.white : SA.borderGray, fontFamily: "'Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.label, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600, transition: "all 0.2s" }}>
              {saving ? "Submitting..." : "Submit"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
